import { FichierAnnuaireDocumentDto } from "@common/services/fichier";
import { Store } from '@reduxjs/toolkit';
import { buildPageableParams, Page, PageableArgs } from '../common';
import { queryUntilLastPage } from '../common/pageable/query-until-last-page';
import { servicesApi } from '../services';
import {
  OrganismeDetailsDto,
  OrganismeDto,
  OrganismeFichierProjection,
  OrganismeInscriptionDto,
  OrganismeProjection,
  OrganismeStatutType,
} from './types';

const ORGANISMES_REQUEST_URL = 'organismes';

const organismeApi = servicesApi.injectEndpoints!({
  endpoints: (build) => ({

    registerOrganisme: build.mutation<void, RegisterOrganismeMutationArgs, any>({
      query: ({ body }) => ({
        url: ORGANISMES_REQUEST_URL,
        method: 'POST',
        body,
      }),
    }),

    updateOrganismeRegistration: build.mutation<void, UpdateOrganismeRegistrationMutationArgs, any>({
      query: ({ id, body }) => ({
        url: `${ORGANISMES_REQUEST_URL}/${id}/inscription`,
        method: 'PUT',
        body,
      }),
    }),

    getOrganismes: build.query<Page<OrganismeDto>, PageableArgs<OrganismeProjection>, any>({
      query: (args = {}) => ({
        url: ORGANISMES_REQUEST_URL,
        params: buildPageableParams(args),
      }),
      providesTags: ['Organismes'],
    }),

    getOrganisme: build.query<OrganismeDetailsDto, GetOrganismeArgs, any>({
      query: ({ id, projection }) => ({
        url: `${ORGANISMES_REQUEST_URL}/${id}`,
        params: { projection },
      }),
      providesTags: ['Organisme'],
    }),

    updateOrganisme: build.mutation<void, UpdateOrganismeArgs, any>({
      query: ({ id, dto }) => ({
        url: `${ORGANISMES_REQUEST_URL}/${id}`,
        method: 'PATCH',
        body: dto,
      }),
      invalidatesTags: ['Organisme'],
    }),

    updateOrganismesDepot: build.mutation<void, UpdateOrganismesDepotArgs, any>({
      query: ({ ids, depotProlonge }) => ({
        url: `${ORGANISMES_REQUEST_URL}/depot`,
        method: 'PATCH',
        body: { ids, depotProlonge },
      }),
      invalidatesTags: ['Organismes', 'Etablissements'],
    }),

    updateOrganismesStatus: build.mutation<void, UpdateOrganismesStatutArgs, any>({
      query: ({ ids, status, motifMailContenu }) => ({
        url: `${ORGANISMES_REQUEST_URL}/statut`,
        method: 'PATCH',
        params: { statut: status },
        body: { ids, motifMailContenu },
        meta: { skipErrorToast: true },
      }),
      invalidatesTags: [
        'Organisme',
        'Organismes', // Invalide le tableau vue SIREN quand on bloque/débloque le dépôt
        'Etablissements', // Invalide le tableau vue SIRET quand on bloque/débloque le dépôt
      ],
    }),

    isSiretAvailable: build.query<boolean, isSiretAvailableArgs, any>({
      query: ({ id, siret }) => `${ORGANISMES_REQUEST_URL}/${id}/siret/${siret}/availability`,
    }),

    getAllOrganismeStates: build.query({
      queryFn: ({ siren }, _api, _extraOptions, baseQuery) => queryUntilLastPage({
        baseQuery,
        url: ORGANISMES_REQUEST_URL,
        params: { siren, projection: OrganismeProjection.STATE },
      }),
      providesTags: ['Organisme'],
    }),

    resendHabilitation: build.mutation<void, ResendHabilitationArgs, any>({
      query: ({ id }) => ({
        url: `${ORGANISMES_REQUEST_URL}/${id}/approbations`,
        method: 'POST',
      }),
      invalidatesTags: ['Organisme'],
    }),

    getExtOrganisme: build.query<OrganismeInscriptionDto, GetExtOrganismeQueryArgs, any>({
      query: ({ siren }) => `${ORGANISMES_REQUEST_URL}/external/${siren}`,
    }),

    countOrganismeDepotProlonge: build.query<number, CountOrganismeDepotProlongeQueryArgs, any>({
      query: ({ campagneId }) => ({
        url: `${ORGANISMES_REQUEST_URL}/count/depot-prolonge`,
        params: { campagneId },
      }),
      providesTags: ['Organismes', 'Etablissements'],
    }),

    getFichiers: build.query<Page<FichierAnnuaireDocumentDto>, PageableArgs<OrganismeFichierProjection>, any>({
      query: (args = {}) => ({
        url: `${ORGANISMES_REQUEST_URL}/fichiers`,
        params: buildPageableParams(args),
      }),
    }),

    getOrganismeForCampagne: build.query<OrganismeDetailsDto, GetOrganismeForCampagneArgs, any>({
      query: ({ siren, campagneId }) => `${ORGANISMES_REQUEST_URL}/siren/${siren}/campagnes/${campagneId}`,
    })
  }),
});

interface GetOrganismeForCampagneArgs {
  siren: string;
  campagneId: number;
}

interface RegisterOrganismeMutationArgs {
  body: FormData; // data: OrganismeInscriptionData; attestation: File; delegation: File;
}

interface UpdateOrganismeRegistrationMutationArgs {
  id: number;
  body: FormData; // data: OrganismeInscriptionData; attestation: File; delegation: File;
}

interface GetExtOrganismeQueryArgs {
  siren: string;
}

interface UpdateOrganismesDepotArgs {
  ids: number[];
  depotProlonge: boolean;
}

export interface UpdateOrganismeArgs {
  id: number;
  dto: Partial<OrganismeDto>;
}

interface GetOrganismeArgs {
  id: number;
  projection: OrganismeProjection;
}

export interface UpdateOrganismesStatutArgs {
  ids: number[];
  status: OrganismeStatutType;
  motifMailContenu?: EmailContent;
}

export interface EmailContent {
  destinataires?: string[];
  sujet: string;
  corps: string;
  stepRedirect?: number;
  idMotif?: number; // id du motif utile pour les pieces-jointes
}

interface isSiretAvailableArgs {
  id: number;
  siret: string;
}

interface ResendHabilitationArgs {
  id: number;
}

interface CountOrganismeDepotProlongeQueryArgs {
  campagneId: number;
}

export const {
  useRegisterOrganismeMutation,
  useUpdateOrganismeRegistrationMutation,
  useLazyGetExtOrganismeQuery,
  useGetOrganismesQuery,
  useGetOrganismeQuery,
  useUpdateOrganismeMutation,
  useUpdateOrganismesDepotMutation,
  useLazyIsSiretAvailableQuery,
  useGetAllOrganismeStatesQuery,
  useResendHabilitationMutation,
  useUpdateOrganismesStatusMutation,
  useCountOrganismeDepotProlongeQuery,
  useGetFichiersQuery,
  useGetOrganismeForCampagneQuery,
  useLazyGetOrganismeForCampagneQuery,
} = organismeApi;

export function resetOrganismeCache(store: Store) {
  store.dispatch(organismeApi.util.invalidateTags([
    'Organisme',
    'Etablissements',
    'OrganismeFichiers',
    'OrganismeUtilisateurs',
  ]));
}